import React from 'react';
import { motion } from 'framer-motion';

// Price Elasticity Animation - Shows intelligent price selection with profit optimization
export const PriceElasticityAnimation = ({ feature }) => {
  const [scanComplete, setScanComplete] = React.useState(false);
  const [showOptimalCard, setShowOptimalCard] = React.useState(false);

  React.useEffect(() => {
    // Trigger scan completion after curve animation
    const timer1 = setTimeout(() => setScanComplete(true), 3500);
    // Show optimal price card after highlighting
    const timer2 = setTimeout(() => setShowOptimalCard(true), 4500);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  // Price points data for the curve (simulating profit vs price relationship)
  const pricePoints = [
    { x: 40, y: 220, price: 25, profit: 45 },
    { x: 70, y: 180, price: 30, profit: 85 },
    { x: 100, y: 140, price: 35, profit: 140 },
    { x: 130, y: 110, price: 40, profit: 185 },
    { x: 160, y: 90, price: 45, profit: 245 },
    { x: 190, y: 85, price: 47, profit: 312 }, // Optimal point
    { x: 220, y: 95, price: 50, profit: 285 },
    { x: 250, y: 120, price: 55, profit: 220 },
    { x: 280, y: 160, price: 60, profit: 165 },
  ];

  const optimalIndex = 5; // Index of optimal price point

  return (
    <div className="price-elasticity-visual" style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      <svg width="100%" height="100%" viewBox="0 0 320 280" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="profitGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={feature?.color || '#3d5a80'} stopOpacity="0.3" />
            <stop offset="100%" stopColor={feature?.color || '#3d5a80'} stopOpacity="0.05" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Grid lines */}
        {[...Array(6)].map((_, i) => (
          <motion.line 
            key={`h-${i}`}
            x1="30" 
            y1={60 + i * 35} 
            x2="290" 
            y2={60 + i * 35} 
            stroke="rgba(61,90,128,0.1)" 
            strokeWidth="1"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.8, delay: 0.1 * i }}
          />
        ))}
        
        {[...Array(8)].map((_, i) => (
          <motion.line 
            key={`v-${i}`}
            x1={40 + i * 35} 
            y1="60" 
            x2={40 + i * 35} 
            y2="235" 
            stroke="rgba(61,90,128,0.1)" 
            strokeWidth="1"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.8, delay: 0.1 * i }}
          />
        ))}

        {/* Axes */}
        <motion.line
          x1="30" y1="235" x2="290" y2="235"
          stroke="rgba(61,90,128,0.25)"
          strokeWidth="2"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        />
        <motion.line
          x1="30" y1="60" x2="30" y2="235"
          stroke="rgba(61,90,128,0.25)"
          strokeWidth="2"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        />

        {/* Axis labels */}
        <motion.text
          x="160" y="255"
          fill="rgba(41,50,65,0.6)"
          fontSize="12"
          textAnchor="middle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          Price (€)
        </motion.text>
        <motion.text
          x="15" y="150"
          fill="rgba(41,50,65,0.6)"
          fontSize="12"
          textAnchor="middle"
          transform="rotate(-90 15 150)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          Expected Profit
        </motion.text>

        {/* Profit curve - animated from left to right */}
        <motion.path
          d={`M${pricePoints.map(p => `${p.x},${p.y}`).join(' L')}`}
          fill="none"
          stroke={feature?.color || '#3d5a80'}
          strokeWidth="3"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2.5, delay: 1.2, ease: "easeInOut" }}
        />

        {/* Area under curve */}
        <motion.path
          d={`M${pricePoints[0].x},${pricePoints[0].y} ${pricePoints.map(p => `L${p.x},${p.y}`).join(' ')} L${pricePoints[pricePoints.length-1].x},235 L${pricePoints[0].x},235 Z`}
          fill="url(#profitGradient)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 2.5 }}
        />

                 {/* Price points with pulsing animation */}
         {pricePoints.map((point, i) => (
           <g key={`point-${i}`}>
             {/* Point circle */}
             <motion.circle
               cx={point.x}
               cy={point.y}
               r="5"
               fill={scanComplete && i === optimalIndex ? '#ee6c4d' : '#fff'}
               stroke={scanComplete && i === optimalIndex ? '#ee6c4d' : feature?.color || '#3d5a80'}
               strokeWidth="2"
               filter={scanComplete && i === optimalIndex ? "url(#glow)" : "none"}
               initial={{ scale: 0, opacity: 0 }}
               animate={{ 
                 scale: scanComplete && i === optimalIndex ? 1.5 : 1,
                 opacity: scanComplete && i !== optimalIndex ? 0.3 : 1
               }}
               transition={{ 
                 delay: 1.5 + i * 0.2,
                 duration: 0.3,
                 scale: { type: "spring", stiffness: 300, damping: 20 },
                 opacity: { duration: 0.5, delay: scanComplete ? 0 : 1.5 + i * 0.2 }
               }}
             />

             {/* Pulse effect for each point as it appears */}
             <motion.circle
               cx={point.x}
               cy={point.y}
               r="5"
               fill="none"
               stroke={feature?.color || '#3d5a80'}
               strokeWidth="2"
               initial={{ scale: 1, opacity: 0.8 }}
               animate={{ scale: 3, opacity: 0 }}
               transition={{ 
                 delay: 1.5 + i * 0.2,
                 duration: 0.8,
                 ease: "easeOut"
               }}
             />

             {/* Floating profit estimates */}
             <motion.g
               initial={{ opacity: 0, y: 10 }}
               animate={{ 
                 opacity: scanComplete && i !== optimalIndex ? 0.4 : 1, 
                 y: 0 
               }}
               transition={{ 
                 delay: 1.7 + i * 0.2,
                 duration: 0.5,
                 type: "spring",
                 stiffness: 200,
                 opacity: { duration: 0.5, delay: scanComplete ? 0 : 1.7 + i * 0.2 }
               }}
             >
               <motion.rect
                 x={point.x - 20}
                 y={point.y - 35}
                 width="40"
                 height="20"
                 rx="10"
                 fill="rgba(255,255,255,0.92)"
                 stroke={scanComplete && i === optimalIndex ? '#ee6c4d' : feature?.color || '#3d5a80'}
                 strokeWidth="1"
                 initial={{ scale: 0 }}
                 animate={{ 
                   scale: 1,
                   opacity: scanComplete && i !== optimalIndex ? 0.4 : 1
                 }}
                 transition={{ 
                   delay: 1.7 + i * 0.2,
                   type: "spring",
                   stiffness: 300,
                   opacity: { duration: 0.5, delay: scanComplete ? 0 : 1.7 + i * 0.2 }
                 }}
               />
               <motion.text
                 x={point.x}
                 y={point.y - 22}
                 fill={scanComplete && i === optimalIndex ? '#ee6c4d' : feature?.color || '#3d5a80'}
                 fontSize="10"
                 fontWeight="bold"
                 textAnchor="middle"
                 initial={{ opacity: 0 }}
                 animate={{ 
                   opacity: scanComplete && i !== optimalIndex ? 0.4 : 1
                 }}
                 transition={{ 
                   delay: 1.8 + i * 0.2,
                   duration: 0.5,
                   opacity: { duration: 0.5, delay: scanComplete ? 0 : 1.8 + i * 0.2 }
                 }}
               >
                 +€{point.profit}
               </motion.text>
             </motion.g>
           </g>
         ))}

        {/* Optimal point highlight ring */}
        {scanComplete && (
          <motion.circle
            cx={pricePoints[optimalIndex].x}
            cy={pricePoints[optimalIndex].y}
            r="12"
            fill="none"
            stroke="#ee6c4d"
            strokeWidth="2"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.6 }}
            transition={{ 
              delay: 0.2,
              type: "spring",
              stiffness: 200,
              damping: 15
            }}
          />
        )}
      </svg>

      {/* Scanning indicator */}
      <motion.div
        style={{
          position: 'absolute',
          top: '15px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(10px)',
          padding: '8px 16px',
          borderRadius: '20px',
          border: `1px solid ${feature?.color || '#3d5a80'}`,
          color: feature?.color || '#3d5a80',
          fontSize: '12px',
          fontWeight: 'bold'
        }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: scanComplete ? 0 : 1, y: 0 }}
        transition={{ 
          duration: 0.3, 
          delay: 1,
          opacity: { duration: 0.3 }
        }}
      >
        🔍 Analyzing price points...
      </motion.div>

      {/* Optimal price recommendation card */}
      {showOptimalCard && (
        <motion.div
          style={{
            position: 'absolute',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(15px)',
            padding: '16px 20px',
            borderRadius: '12px',
            border: '2px solid #ee6c4d',
            boxShadow: '0 0 20px rgba(238,108,77,0.3)',
            textAlign: 'center',
            minWidth: '250px'
          }}
          initial={{ opacity: 0, y: 50, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ 
            type: "spring",
            stiffness: 200,
            damping: 20
          }}
        >
          <div style={{ color: '#ee6c4d', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
            ✨ Optimal Price Found
          </div>
          <div style={{ color: '#293241', fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>
            Set price to €47
          </div>
          <div style={{ color: '#ee6c4d', fontSize: '12px' }}>
            Expected +€312 profit vs current
          </div>
        </motion.div>
      )}

      {/* Analysis complete indicator */}
      {scanComplete && (
        <motion.div
          style={{
            position: 'absolute',
            top: '15px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(238,108,77,0.1)',
            backdropFilter: 'blur(10px)',
            padding: '8px 16px',
            borderRadius: '20px',
            border: '1px solid #ee6c4d',
            color: '#ee6c4d',
            fontSize: '12px',
            fontWeight: 'bold'
          }}
          initial={{ opacity: 0, scale: 0.8, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ 
            type: "spring",
            stiffness: 300,
            damping: 20,
            delay: 0.4
          }}
        >
          ✅ Analysis Complete
        </motion.div>
      )}
    </div>
  );
};

// Predictive Analytics Chart Animation - Shows price/margin optimization curves
export const PredictiveAnalyticsAnimation = ({ feature }) => {
  return (
    <div className="predictive-visual" style={{ position: 'relative', height: '100%' }}>
        {/* Graph visualization */}
        <svg width="100%" height="100%" viewBox="0 0 300 300" style={{ position: 'absolute' }}>
        <defs>
            <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={feature.color} stopOpacity="0.8" />
            <stop offset="100%" stopColor={feature.color} stopOpacity="0.1" />
            </linearGradient>
        </defs>
        
        {/* Grid lines */}
        {[...Array(6)].map((_, i) => (
            <motion.line 
            key={`h-${i}`}
            x1="40" 
            y1={50 + i * 40} 
            x2="260" 
            y2={50 + i * 40} 
            stroke="rgba(61,90,128,0.1)" 
            strokeWidth="1"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1, delay: 0.1 * i }}
            />
        ))}
        
        {[...Array(6)].map((_, i) => (
            <motion.line 
            key={`v-${i}`}
            x1={40 + i * 44} 
            y1="50" 
            x2={40 + i * 44} 
            y2="250" 
            stroke="rgba(61,90,128,0.1)" 
            strokeWidth="1"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1, delay: 0.1 * i }}
            />
        ))}
        
        {/* Line chart */}
        <motion.path
            d="M40,200 C80,180 100,220 140,160 S200,100 220,140 S240,110 260,90"
            fill="none"
            stroke={feature.color}
            strokeWidth="3"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2 }}
        />
        
        {/* Area under chart */}
        <motion.path
            d="M40,200 C80,180 100,220 140,160 S200,100 220,140 S240,110 260,90 V250 H40 Z"
            fill="url(#chartGradient)"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
        />
        
        {/* Forecast dotted line */}
        <motion.path
            d="M260,90 C280,70 290,60 300,50"
            fill="none"
            stroke={feature.color}
            strokeWidth="3"
            strokeDasharray="5,5"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1, delay: 2 }}
        />
        
        {/* Data points */}
        {[
            {x: 40, y: 200}, {x: 84, y: 180}, {x: 128, y: 190}, 
            {x: 172, y: 120}, {x: 216, y: 140}, {x: 260, y: 90}
        ].map((point, i) => (
            <motion.circle
            key={`point-${i}`}
            cx={point.x}
            cy={point.y}
            r="6"
            fill="#3d5a80"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5 + i * 0.2 }}
            />
        ))}
        </svg>
        
        {/* Floating data labels */}
        <motion.div 
        style={{ 
            position: 'absolute', 
            top: '30%', 
            right: '25%',
            background: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(10px)',
            padding: '10px 15px',
            borderRadius: '10px',
            border: `1px solid ${feature.color}`
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
        >
        <div style={{ color: feature.color, fontWeight: 'bold' }}>+42% Growth</div>
        </motion.div>
        
        <motion.div 
        style={{ 
            position: 'absolute', 
            bottom: '25%', 
            left: '30%',
            background: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(10px)',
            padding: '10px 15px',
            borderRadius: '10px',
            border: `1px solid ${feature.color}`
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.8 }}
        >
        <div style={{ color: feature.color, fontWeight: 'bold' }}>Upward Trend Identified</div>
        </motion.div>
    </div>
  );
};

// StrataMemory Neural Network Animation - Shows learning and memory patterns
export const StrataMemoryAnimation = ({ feature }) => {
  return (
    <div className="strataMemory-visual" style={{ position: 'relative', height: '100%' }}>
        {/* Neural network visualization */}
        <svg width="100%" height="100%" viewBox="0 0 300 300" style={{ position: 'absolute' }}>
        {/* Hexagon network nodes */}
        {[
            {x: 150, y: 60, size: 28},  // Top
            {x: 90, y: 95, size: 25},   // Top Left
            {x: 210, y: 95, size: 25},  // Top Right
            {x: 60, y: 150, size: 25},  // Left
            {x: 240, y: 150, size: 25}, // Right
            {x: 90, y: 205, size: 25},  // Bottom Left
            {x: 210, y: 205, size: 25}, // Bottom Right
            {x: 150, y: 240, size: 28}, // Bottom
            {x: 150, y: 150, size: 35}, // Center
        ].map((node, i) => (
            <g key={`hex-${i}`}>
            <motion.polygon
                points={`
                ${node.x},${node.y - node.size} 
                ${node.x + node.size * 0.866},${node.y - node.size * 0.5} 
                ${node.x + node.size * 0.866},${node.y + node.size * 0.5} 
                ${node.x},${node.y + node.size} 
                ${node.x - node.size * 0.866},${node.y + node.size * 0.5} 
                ${node.x - node.size * 0.866},${node.y - node.size * 0.5}
                `}
                fill="rgba(61,90,128,0.08)"
                stroke={feature.color}
                strokeWidth="2"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
            />
            <motion.circle
                cx={node.x}
                cy={node.y}
                r={node.size * 0.3}
                fill={feature.color}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: i * 0.12 + 0.3 }}
            />
            </g>
        ))}
        
        {/* Connection lines */}
        {[
            // Outer heptagon connections
            {x1: 150, y1: 60, x2: 90, y2: 95},    // Top to Top Left
            {x1: 150, y1: 60, x2: 210, y2: 95},   // Top to Top Right
            {x1: 90, y1: 95, x2: 60, y2: 150},    // Top Left to Left
            {x1: 210, y1: 95, x2: 240, y2: 150},  // Top Right to Right
            {x1: 60, y1: 150, x2: 90, y2: 205},   // Left to Bottom Left
            {x1: 240, y1: 150, x2: 210, y2: 205}, // Right to Bottom Right
            {x1: 90, y1: 205, x2: 150, y2: 240},  // Bottom Left to Bottom
            {x1: 210, y1: 205, x2: 150, y2: 240}, // Bottom Right to Bottom
            
            // Connections to center
            {x1: 150, y1: 60, x2: 150, y2: 150},  // Top to Center
            {x1: 90, y1: 95, x2: 150, y2: 150},   // Top Left to Center
            {x1: 210, y1: 95, x2: 150, y2: 150},  // Top Right to Center
            {x1: 60, y1: 150, x2: 150, y2: 150},  // Left to Center
            {x1: 240, y1: 150, x2: 150, y2: 150}, // Right to Center
            {x1: 90, y1: 205, x2: 150, y2: 150},  // Bottom Left to Center
            {x1: 210, y1: 205, x2: 150, y2: 150}, // Bottom Right to Center
            {x1: 150, y1: 240, x2: 150, y2: 150}, // Bottom to Center
        ].map((line, i) => (
            <motion.line
            key={`line-${i}`}
            x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2}
            stroke={feature.color}
            strokeOpacity="0.7"
            strokeWidth="2"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.7 }}
            transition={{ duration: 0.8, delay: 0.8 + i * 0.08 }}
            />
        ))}
        
        {/* Data pulses - multiple dots flying in from outside */}
        {/* Top node incoming data */}
        {[...Array(3)].map((_, i) => {
            const startAngle = Math.PI * (0.8 + Math.random() * 0.4); // Random angle from top
            const startDistance = 150 + Math.random() * 100;
            const startX = 150 + Math.cos(startAngle) * startDistance;
            const startY = 60 + Math.sin(startAngle) * startDistance;
            
            return (
            <motion.circle
                key={`pulse-top-in-${i}`}
                cx={startX}
                cy={startY}
                r={4 + Math.random() * 3}
                fill="#3d5a80"
                initial={{ scale: 0 }}
                animate={{ 
                scale: [0, 1, 1, 0],
                cx: [startX, 150, 150],
                cy: [startY, 60, 60],
                }}
                transition={{ 
                duration: 2,
                delay: i * 2.5 + Math.random() * 2,
                repeat: Infinity,
                repeatDelay: 5 + Math.random() * 5,
                times: [0, 0.4, 0.6, 1]
                }}
            />
            );
        })}
        
        {/* Top Left node incoming data */}
        {[...Array(2)].map((_, i) => {
            const startAngle = Math.PI * (1.2 + Math.random() * 0.3); // Random angle from left
            const startDistance = 100 + Math.random() * 80;
            const startX = 90 + Math.cos(startAngle) * startDistance;
            const startY = 95 + Math.sin(startAngle) * startDistance;

            return (
            <motion.circle
                key={`pulse-topleft-in-${i}`}
                cx={startX}
                cy={startY}
                r={4 + Math.random() * 3}
                fill="#3d5a80"
                initial={{ scale: 0 }}
                animate={{ 
                scale: [0, 1, 1, 0],
                cx: [startX, 90, 90],
                cy: [startY, 95, 95],
                }}
                transition={{
                duration: 2,
                delay: i * 3 + Math.random() * 3 + 1,
                repeat: Infinity,
                repeatDelay: 6 + Math.random() * 4,
                times: [0, 0.4, 0.6, 1]
                }}
            />
            );
        })}
        
        {/* Top Right node incoming data */}
        {[...Array(2)].map((_, i) => {
            const startAngle = Math.PI * (0.5 + Math.random() * 0.3); // Random angle from right
            const startDistance = 100 + Math.random() * 80;
            const startX = 210 + Math.cos(startAngle) * startDistance;
            const startY = 95 + Math.sin(startAngle) * startDistance;
            
            return (
            <motion.circle
                key={`pulse-topright-in-${i}`}
                cx={startX}
                cy={startY}
                r={4 + Math.random() * 3}
                fill="#3d5a80"
                initial={{ scale: 0 }}
                animate={{ 
                scale: [0, 1, 1, 0],
                cx: [startX, 210, 210],
                cy: [startY, 95, 95],
                }}
                transition={{
                duration: 2,
                delay: i * 2.7 + Math.random() * 2 + 0.5,
                repeat: Infinity,
                repeatDelay: 5 + Math.random() * 5,
                times: [0, 0.4, 0.6, 1]
                }}
            />
            );
        })}
        
        {/* Left node incoming data */}
        {[...Array(2)].map((_, i) => {
            const startAngle = Math.PI * (1.3 + Math.random() * 0.4); // Random angle from left
            const startDistance = 100 + Math.random() * 80;
            const startX = 60 + Math.cos(startAngle) * startDistance;
            const startY = 150 + Math.sin(startAngle) * startDistance;
            
            return (
            <motion.circle
                key={`pulse-left-in-${i}`}
                cx={startX}
                cy={startY}
                r={4 + Math.random() * 3}
                fill="#3d5a80"
                initial={{ scale: 0 }}
                animate={{ 
                scale: [0, 1, 1, 0],
                cx: [startX, 60, 60],
                cy: [startY, 150, 150],
                }}
                transition={{ 
                duration: 2, 
                delay: i * 3.1 + Math.random() * 2 + 1.3,
                repeat: Infinity,
                repeatDelay: 6 + Math.random() * 4,
                times: [0, 0.4, 0.6, 1]
                }}
            />
            );
        })}
        
        {/* Right node incoming data */}
        {[...Array(2)].map((_, i) => {
            const startAngle = Math.PI * (0.3 + Math.random() * 0.4); // Random angle from right
            const startDistance = 100 + Math.random() * 80;
            const startX = 240 + Math.cos(startAngle) * startDistance;
            const startY = 150 + Math.sin(startAngle) * startDistance;
            
            return (
            <motion.circle
                key={`pulse-right-in-${i}`}
                cx={startX}
                cy={startY}
                r={4 + Math.random() * 3}
                fill="#3d5a80"
                initial={{ scale: 0 }}
                animate={{
                scale: [0, 1, 1, 0],
                cx: [startX, 240, 240],
                cy: [startY, 150, 150],
                }}
                transition={{
                duration: 2,
                delay: i * 2.8 + Math.random() * 2 + 1.1,
                repeat: Infinity,
                repeatDelay: 5 + Math.random() * 5,
                times: [0, 0.4, 0.6, 1]
                }}
            />
            );
        })}
        
        {/* Bottom Left node incoming data */}
        {[...Array(2)].map((_, i) => {
            const startAngle = Math.PI * (1.2 + Math.random() * 0.3); // Random angle from left
            const startDistance = 100 + Math.random() * 80;
            const startX = 90 + Math.cos(startAngle) * startDistance;
            const startY = 205 + Math.sin(startAngle) * startDistance;
            
            return (
            <motion.circle
                key={`pulse-bottomleft-in-${i}`}
                cx={startX}
                cy={startY}
                r={4 + Math.random() * 3}
                fill="#3d5a80"
                initial={{ scale: 0 }}
                animate={{ 
                scale: [0, 1, 1, 0],
                cx: [startX, 90, 90],
                cy: [startY, 205, 205],
                }}
                transition={{ 
                duration: 2,
                delay: i * 3.2 + Math.random() * 2 + 1.5,
                repeat: Infinity,
                repeatDelay: 6 + Math.random() * 4,
                times: [0, 0.4, 0.6, 1]
                }}
            />
            );
        })}
        
        {/* Bottom Right node incoming data */}
        {[...Array(2)].map((_, i) => {
            const startAngle = Math.PI * (0.5 + Math.random() * 0.3); // Random angle from right
            const startDistance = 100 + Math.random() * 80;
            const startX = 210 + Math.cos(startAngle) * startDistance;
            const startY = 205 + Math.sin(startAngle) * startDistance;

            return (
            <motion.circle
                key={`pulse-bottomright-in-${i}`}
                cx={startX}
                cy={startY}
                r={4 + Math.random() * 3}
                fill="#3d5a80"
                initial={{ scale: 0 }}
                animate={{ 
                scale: [0, 1, 1, 0],
                cx: [startX, 210, 210],
                cy: [startY, 205, 205],
                }}
                transition={{
                duration: 2,
                delay: i * 2.9 + Math.random() * 2 + 1.2,
                repeat: Infinity,
                repeatDelay: 5 + Math.random() * 5,
                times: [0, 0.4, 0.6, 1]
                }}
            />
            );
        })}
        
        {/* Bottom node incoming data */}
        {[...Array(3)].map((_, i) => {
            const startAngle = Math.PI * (0.2 + Math.random() * 0.6); // Random angle from bottom
            const startDistance = 150 + Math.random() * 100;
            const startX = 150 + Math.cos(startAngle) * startDistance;
            const startY = 240 + Math.sin(startAngle) * startDistance;
            
            return (
            <motion.circle
                key={`pulse-bottom-in-${i}`}
                cx={startX}
                cy={startY}
                r={4 + Math.random() * 3}
                fill="#3d5a80"
                initial={{ scale: 0 }}
                animate={{ 
                scale: [0, 1, 1, 0],
                cx: [startX, 150, 150],
                cy: [startY, 240, 240],
                }}
                transition={{
                duration: 2,
                delay: i * 2.6 + Math.random() * 2 + 0.8,
                repeat: Infinity,
                repeatDelay: 5 + Math.random() * 5,
                times: [0, 0.4, 0.6, 1]
                }}
            />
            );
        })}
        
        {/* Center node incoming data */}
        {[...Array(3)].map((_, i) => {
            const startAngle = Math.PI * 2 * Math.random(); // Random angle from any direction
            const startDistance = 120 + Math.random() * 80;
            const startX = 150 + Math.cos(startAngle) * startDistance;
            const startY = 150 + Math.sin(startAngle) * startDistance;
            
            return (
            <motion.circle
                key={`pulse-center-in-${i}`}
                cx={startX}
                cy={startY}
                r={4 + Math.random() * 3}
                fill="#3d5a80"
                initial={{ scale: 0 }}
                animate={{ 
                scale: [0, 1, 1, 0],
                cx: [startX, 150, 150],
                cy: [startY, 150, 150],
                }}
                transition={{ 
                duration: 2,
                delay: i * 3.5 + Math.random() * 2 + 1,
                repeat: Infinity,
                repeatDelay: 7 + Math.random() * 3,
                times: [0, 0.4, 0.6, 1]
                }}
            />
            );
        })}
        
        {/* Data flowing through the network */}
        {/* Adjacent node connections */}
        {[
            {x1: 150, y1: 60, x2: 90, y2: 95, delay: 2.1},    // Top to Top Left
            {x1: 150, y1: 60, x2: 210, y2: 95, delay: 2.4},   // Top to Top Right
            {x1: 90, y1: 95, x2: 60, y2: 150, delay: 2.7},    // Top Left to Left
            {x1: 210, y1: 95, x2: 240, y2: 150, delay: 3.0},  // Top Right to Right
            {x1: 60, y1: 150, x2: 90, y2: 205, delay: 3.3},   // Left to Bottom Left
            {x1: 240, y1: 150, x2: 210, y2: 205, delay: 3.6}, // Right to Bottom Right
            {x1: 90, y1: 205, x2: 150, y2: 240, delay: 3.9},  // Bottom Left to Bottom
            {x1: 210, y1: 205, x2: 150, y2: 240, delay: 4.2}, // Bottom Right to Bottom
        ].map((conn, i) => (
            <motion.circle
            key={`pulse-adjacent-${i}`}
            cx={conn.x1}
            cy={conn.y1}
            r="4"
            fill="#3d5a80"
            initial={{ scale: 0 }}
            animate={{ 
                scale: [0, 1, 0],
                cx: [conn.x1, conn.x2],
                cy: [conn.y1, conn.y2],
            }}
            transition={{
                duration: 1,
                delay: conn.delay,
                repeat: Infinity,
                repeatDelay: 8
            }}
            />
        ))}
        
        {/* Connections to center from all nodes */}
        {[
            {x1: 150, y1: 60, x2: 150, y2: 150, delay: 4.5},  // Top to Center
            {x1: 90, y1: 95, x2: 150, y2: 150, delay: 4.8},   // Top Left to Center
            {x1: 210, y1: 95, x2: 150, y2: 150, delay: 5.1},  // Top Right to Center
            {x1: 60, y1: 150, x2: 150, y2: 150, delay: 5.4},  // Left to Center
            {x1: 240, y1: 150, x2: 150, y2: 150, delay: 5.7}, // Right to Center
            {x1: 90, y1: 205, x2: 150, y2: 150, delay: 6.0},  // Bottom Left to Center
            {x1: 210, y1: 205, x2: 150, y2: 150, delay: 6.3}, // Bottom Right to Center
            {x1: 150, y1: 240, x2: 150, y2: 150, delay: 6.6}, // Bottom to Center
        ].map((conn, i) => (
            <motion.circle
            key={`pulse-to-center-${i}`}
            cx={conn.x1}
            cy={conn.y1}
            r="4"
            fill="#3d5a80"
            initial={{ scale: 0 }}
            animate={{ 
                scale: [0, 1, 0],
                cx: [conn.x1, conn.x2],
                cy: [conn.y1, conn.y2],
            }}
            transition={{ 
                duration: 1,
                delay: conn.delay,
                repeat: Infinity,
                repeatDelay: 10
            }}
            />
        ))}
        
        {/* Data leaving the network - from bottom node */}
        {[...Array(4)].map((_, i) => {
            const angle = Math.PI * (0.3 + Math.random() * 0.4); // Random angle downward
            const endDistance = 100 + Math.random() * 80;
            const endX = 150 + Math.cos(angle) * endDistance;
            const endY = 240 + Math.sin(angle) * endDistance;

            return (
            <motion.circle
                key={`pulse-bottom-out-${i}`}
                cx={150}
                cy={240}
                r={4 + Math.random() * 3}
                fill="#3d5a80"
                initial={{ scale: 0 }}
                animate={{ 
                scale: [0, 1, 0],
                cx: [150, endX],
                cy: [240, endY],
                }}
                transition={{ 
                duration: 2,
                delay: i * 3 + 7 + Math.random() * 2,
                repeat: Infinity,
                repeatDelay: 7 + Math.random() * 3
                }}
            />
            );
        })}
        
        {/* Data leaving from center node */}
        {[...Array(4)].map((_, i) => {
            const angle = Math.PI * (1.7 + Math.random() * 0.6); // Random angle upward
            const endDistance = 120 + Math.random() * 80;
            const endX = 150 + Math.cos(angle) * endDistance;
            const endY = 150 + Math.sin(angle) * endDistance;

            return (
            <motion.circle
                key={`pulse-center-out-${i}`}
                cx={150}
                cy={150}
                r={4 + Math.random() * 3}
                fill="#3d5a80"
                initial={{ scale: 0 }}
                animate={{ 
                scale: [0, 1, 0],
                cx: [150, endX],
                cy: [150, endY],
                }}
                transition={{ 
                duration: 2,
                delay: i * 3 + 6 + Math.random() * 2,
                repeat: Infinity,
                repeatDelay: 7 + Math.random() * 3
                }}
            />
            );
        })}
        
        {/* Binary data bits floating around */}
        {Array.from({ length: 20 }).map((_, i) => {
            const x = 50 + Math.random() * 200;
            const y = 50 + Math.random() * 200;

            return (
            <motion.text
                key={`bit-${i}`}
                x={x}
                y={y}
                fill="rgba(41,50,65,0.35)"
                fontSize="8"
                fontFamily="monospace"
                initial={{ opacity: 0 }}
                animate={{ 
                opacity: [0, 0.6, 0],
                }}
                transition={{
                duration: 3,
                delay: Math.random() * 5,
                repeat: Infinity,
                repeatDelay: Math.random() * 5,
                }}
            >
                {Math.random() > 0.5 ? "1" : "0"}
            </motion.text>
            );
        })}
        </svg>
        
        {/* Memory label */}
        <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        style={{
            position: 'absolute',
            bottom: '10%',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(10px)',
            padding: '8px 16px',
            borderRadius: '30px',
            border: `1px solid ${feature.color}30`,
            color: feature.color,
            fontWeight: 'bold',
            textAlign: 'center'
        }}
        >
        Continuous Learning System
        </motion.div>
    </div>
  );
};

// AI Insights Core Animation - Shows AI processing and insights generation
export const AIInsightsAnimation = ({ feature }) => {
  return (
    <div className="insights-visual" style={{ position: 'relative', height: '100%' }}>
        <div className="ai-core">
        <div className="core-pulse" style={{ 
            borderColor: feature.color,
            boxShadow: `0 0 30px ${feature.color}50`,
            position: 'absolute',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            border: `2px solid ${feature.color}`,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            animation: 'pulse 3s infinite'
        }}></div>
        <div className="core-center" style={{ 
            background: feature.gradient,
            position: 'absolute',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            color: 'white',
            zIndex: 5
        }}>
            <i className={feature.icon}></i>
        </div>
        
        <div className="connections" style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%'
        }}>
            {[...Array(10)].map((_, i) => {
            const angle = (i * 36) * Math.PI / 180;
            const x1 = 50 + 20 * Math.cos(angle);
            const y1 = 50 + 20 * Math.sin(angle);
            const x2 = 50 + 100 * Math.cos(angle);
            const y2 = 50 + 100 * Math.sin(angle);
            
            return (
                <motion.div 
                key={`connector-${i}`}
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: '100%', opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.05 * i }}
                style={{
                    position: 'absolute',
                    height: '2px',
                    left: `${x1}%`,
                    top: `${y1}%`,
                    width: `${Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2))}%`,
                    transform: `rotate(${angle * 180 / Math.PI}deg)`,
                    transformOrigin: 'left center',
                    background: `linear-gradient(to right, ${feature.color}, transparent)`
                }}
                ></motion.div>
            );
            })}
        </div>
        
        <div className="insight-nodes" style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 3
        }}>
            {[
            { top: '15%', left: '8%', text: 'Real-time Recommendations', delay: 0.8 },
            { top: '75%', left: '12%', text: 'Proactive Suggestions', delay: 1.0 },
            { top: '25%', right: '8%', text: 'Interactive Insights', delay: 1.2 },
            { top: '65%', right: '12%', text: 'Strategy Enhancement', delay: 1.4 },
            ].map((node, i) => (
            <motion.div
                key={`insight-${i}`}
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: node.delay, duration: 0.5 }}
                style={{
                position: 'absolute',
                top: node.top,
                left: node.left,
                right: node.right,
                background: 'rgba(255, 255, 255, 0.92)',
                backdropFilter: 'blur(4px)',
                border: `1px solid ${feature.color}`,
                borderRadius: '8px',
                padding: '6px 12px',
                fontSize: '0.85rem',
                fontWeight: 600,
                whiteSpace: 'nowrap'
                }}
            >
                <span style={{ color: feature.color }}>{node.text}</span>
            </motion.div>
            ))}
        </div>
        </div>
        
        <style jsx>{`
        @keyframes pulse {
            0% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
            50% { transform: translate(-50%, -50%) scale(1.5); opacity: 0.1; }
            100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
        }
        `}</style>
    </div>
  );
};

// Animation mapping object for easy assignment
export const FEATURE_ANIMATIONS = {
  PRICE_ELASTICITY: 'PriceElasticityAnimation',
  PREDICTIVE_ANALYTICS: 'PredictiveAnalyticsAnimation',
  STRATA_MEMORY: 'StrataMemoryAnimation', 
  AI_INSIGHTS: 'AIInsightsAnimation'
};

// Component that renders the appropriate animation based on the animationType
export const FeatureAnimation = ({ feature, animationType }) => {
  switch (animationType) {
    case FEATURE_ANIMATIONS.PRICE_ELASTICITY:
      return <PriceElasticityAnimation feature={feature} />;
    case FEATURE_ANIMATIONS.PREDICTIVE_ANALYTICS:
      return <PredictiveAnalyticsAnimation feature={feature} />;
    case FEATURE_ANIMATIONS.STRATA_MEMORY:
      return <StrataMemoryAnimation feature={feature} />;
    case FEATURE_ANIMATIONS.AI_INSIGHTS:
      return <AIInsightsAnimation feature={feature} />;
    default:
      return <PredictiveAnalyticsAnimation feature={feature} />;
  }
}; 